import {ParamComponent} from 'panel';
import produce from 'immer';
import omit from 'lodash/omit';

import {IronContextRegistry} from 'common/report/context-registry';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {getOnboardingChecklist} from 'common/report/queries/app-api/onboarding-checklist';
import {CurrentProject} from 'common/report/model-resources/projects';
import {getProjectId, getProjectSetupUrl, getReportUrl, getWorkspaceId} from 'common/report/util';
import {LocalStorageKey} from 'common/report/constants';
import {isFoundationalTemplatesEnabled} from 'common/report/util/feature-sampling';

import {template} from './template';
import css from './index.module.styl?inline';
import {
  ChecklistStep,
  OnboardingChecklistJsxProps,
  OnboardingChecklistParams,
  OnboardingChecklistState,
  OnboardingChecklistTrackingEvent,
  PERSISTED_STATE_KEY,
  StepMetadata,
  StepProps,
} from './types';
import {LocalStorageHelper} from './local-storage-helper';

type PersistableState = Omit<OnboardingChecklistState, `showCelebrationPeek` | `showSurvey`>;

export class OnboardingChecklist extends ParamComponent<
  OnboardingChecklistParams,
  OnboardingChecklistState,
  IronContextRegistry
> {
  private completedInSession: boolean;
  private onboardingChecklistStorageHelper: LocalStorageHelper<PersistableState>;
  private intervalId = null;

  private readonly onboardingChecklistRegistry: {[key in ChecklistStep]: StepMetadata} = {
    [ChecklistStep.OrgSetup]: {
      requireImplementation: false,
      title: `Set Up Your Organization`,
      description: `Name your project and organization.`,
      callback: ({completed, orgId}) => {
        if (completed) {
          window.location.href = getReportUrl(`settings/#org/${orgId}`, {projectId: getProjectId()});
        } else {
          window.location.href = `/find-your-org`;
        }
      },
      trackingEventName: OnboardingChecklistTrackingEvent.OrgSetup,
    },
    [ChecklistStep.Implementation]: {
      requireImplementation: false,
      title: `Implement Mixpanel`,
      description: `Send data into to Mixpanel in order to start making boards and reports`,
      callback: () => {
        window.location.href = getProjectSetupUrl();
      },
      trackingEventName: OnboardingChecklistTrackingEvent.Implementation,
    },
    [ChecklistStep.ViewEvent]: {
      requireImplementation: true,
      title: `View Your Data`,
      description: `See your users and events in real time.`,
      callback: () => {
        // Note: this re-mounts the chrome-header and GET /onboarding-checklist is executed again
        window.location.href = getReportUrl(`events`, {projectId: getProjectId(), workspaceId: getWorkspaceId()});
      },
      statusUpdate: {
        [ChecklistStep.ViewEvent]: true,
      },
      trackingEventName: OnboardingChecklistTrackingEvent.ViewEvent,
    },
    [ChecklistStep.CreateBoard]: {
      requireImplementation: true,
      title: `Build Your First Board`,
      description: `Select from one of our templates to get started with making boards and reports. `,
      callback: () => {
        const useFoundationalTemplate = isFoundationalTemplatesEnabled(this.getCurrentProject());

        window.location.href = getReportUrl(
          `boards?${
            useFoundationalTemplate ? `show-template-selector` : `show-event-translator`
          }=true&source=Onboarding Checklist`,
          {
            projectId: getProjectId(),
            workspaceId: getWorkspaceId(),
          },
        );
      },
      trackingEventName: OnboardingChecklistTrackingEvent.CreateBoard,
    },
  };

  get config() {
    return {
      contexts: [`currentProject` as const, `localStorageManager` as const],
      params: {
        shouldCollapse: Boolean,
        viewingBoards: Boolean,
        inviteCallback: Function,
        dismissCallback: Function,
        completeCallback: Function,
        userEmail: String,
      },
      css,
      template,
      useShadowDom: true,
      defaultState: {
        expanded: true,
        status: {
          [ChecklistStep.OrgSetup]: false,
          [ChecklistStep.CreateBoard]: false,
          [ChecklistStep.Implementation]: false,
          [ChecklistStep.ViewEvent]: false,
        },
        showCelebrationPeek: false,
        copiedLink: false,
        showSurvey: false,
      },
      hooks: {
        postUpdate: (stateUpdate) => {
          if (stateUpdate) {
            if (this.completedChecklist() && !this.completedInSession) {
              this.track(OnboardingChecklistTrackingEvent.Completed);
              this.completedInSession = true;
            }

            this.onboardingChecklistStorageHelper.set(
              PERSISTED_STATE_KEY,
              omit(this.state, [`showCelebrationPeek`, `showSurvey`]),
            );
          }

          if (this.params.viewingBoards) {
            this.trySetUpRefreshInterval();
          }

          if (this.intervalId && (this.state.status.CreateBoard || !this.params.viewingBoards)) {
            clearInterval(this.intervalId);
            this.intervalId = null;
          }
        },
      },
    };
  }

  getOrgId(): number {
    return this.getCurrentProject()?.organizationId;
  }

  // TODO (sonya): Add business logic for selecting users to show Calendly link
  showCalendly(): boolean {
    return false;
  }

  completedChecklist(): boolean {
    return Object.values(this.state.status).every((s) => !!s);
  }

  track(eventName: OnboardingChecklistTrackingEvent, props?: {[key: string]: string | number | boolean}): void {
    mixpanel.track(eventName, {
      ...props,
      organization_id: this.getCurrentProject()?.organizationId,
      project_id: getProjectId(),
    });
  }

  shouldComponentUpdate(params: OnboardingChecklistParams, _state: OnboardingChecklistState): boolean {
    if (params && params.shouldCollapse && this.state.expanded) {
      // Update state and defer re-rendering to state update
      this.update({expanded: false, showCelebrationPeek: false});
      return false;
    }

    return true;
  }

  steps(): StepProps[] {
    return Object.keys(this.onboardingChecklistRegistry).map((k: ChecklistStep, index) => {
      return this.convertToStepProps(k, index + 1);
    });
  }

  invite = () => {
    this.track(OnboardingChecklistTrackingEvent.Invite);
    this.params.inviteCallback();
  };

  private convertToStepProps(step: ChecklistStep, label: number): StepProps {
    const completed = this.state.status[step];
    const metadata = this.onboardingChecklistRegistry[step];

    const clickCallback = () => {
      metadata.callback?.({
        completed,
        orgId: this.getCurrentProject().organizationId,
      });

      if (metadata.statusUpdate) {
        /* Delay state update to avoid flicker */
        window.setTimeout(() => {
          if (!metadata.requireImplementation || this.state.status.Implementation) {
            this.update(
              produce<OnboardingChecklistState>(({status}) => {
                Object.keys(metadata.statusUpdate).forEach((k) => {
                  status[k] = metadata.statusUpdate[k];
                });
              }),
            );
          }
        }, 2000);
      }

      this.track(metadata.trackingEventName, {
        'Step Completed': completed,
      });
    };

    return {
      completed,
      label,
      header: metadata.title,
      body: metadata.description,
      onClick: clickCallback,
    };
  }

  getCurrentProject(): CurrentProject {
    return this.getContext(`currentProject`).get().value;
  }

  private async refresh(backgroundRefresh = false): Promise<void> {
    const projectId = getProjectId();

    if (!projectId) {
      return;
    }

    const response = await getOnboardingChecklist(projectId, backgroundRefresh);
    const status = {
      OrgSetup: response.org_ownership,
      Implementation: response.implementation,
      ViewEvent: response.viewed_event,
      CreateBoard: response.board_creation,
    };

    if (!Object.keys(this.state.status).every((k) => this.state.status[k] === status[k])) {
      this.update({
        status,
      });
    }
  }

  private trySetUpRefreshInterval() {
    if (!this.state.status.CreateBoard && this.state.status.Implementation && this.intervalId === null) {
      this.intervalId = setInterval(() => {
        this.refresh(true /* Background refresh */);
      }, 6000);
    }
  }

  async connectedCallback(): Promise<void> {
    this.onboardingChecklistStorageHelper = new LocalStorageHelper({
      context: this.getContext(`localStorageManager`),
      namespace: LocalStorageKey.OnboardingChecklist,
    });

    const persistedState = this.onboardingChecklistStorageHelper.get(PERSISTED_STATE_KEY);

    if (persistedState) {
      this.update(persistedState);
    }

    await this.refresh();

    if (this.params.viewingBoards) {
      this.update({expanded: false});
      this.trySetUpRefreshInterval();
    }

    if (this.completedChecklist()) {
      this.completedInSession = true; // Set this as true at initialization to avoid logging completion
      this.update({showSurvey: true});
    }

    super.connectedCallback();
  }

  disconnectedCallback(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }

    super.disconnectedCallback();
  }
}

export const OnboardingChecklistEl = defineAndMakeJsxFactory<OnboardingChecklistJsxProps>(
  `mp-onboarding-checklist`,
  OnboardingChecklist,
);
export const OnboardingChecklistReact = panel2react<OnboardingChecklistJsxProps>(
  `mp-onboarding-checklist`,
  OnboardingChecklist,
);
